import { useEffect, useState } from 'react'

import { FileRepositoryModel } from 'src/models/file-repository-model'
import { countDirectoryChildren } from 'src/service/fileRepositoryService'

/**
 * For every DIRECTORY entry in the supplied list, fetches the total number
 * of direct children and returns them as a map of `directoryId → count`.
 *
 * The counts are re-fetched whenever the `entries` reference changes.
 */
export function useDirectoryChildCounts(
  entries: FileRepositoryModel[],
  companyId?: string
) {
  const [counts, setCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    let cancelled = false

    const directories = entries.filter((e) => e.type === 'DIRECTORY')

    if (directories.length === 0) {
      setCounts({})
      return
    }

    // Fire all requests in parallel
    Promise.all(
      directories.map(async (dir) => {
        const total = await countDirectoryChildren(dir.id, companyId)
        return { id: dir.id, total }
      })
    ).then((results) => {
      if (cancelled) return
      const map: Record<string, number> = {}
      for (const r of results) {
        map[r.id] = r.total
      }
      setCounts(map)
    })

    return () => {
      cancelled = true
    }
  }, [entries, companyId])

  return counts
}
